import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";

export function useLocalStorageString<T extends string>(key: string): [T, (val: T) => void] {

    const [value, setValue] = useState<T>(localStorage.getItem(key) as T);

    useEffect(() => {
        const handler = (evt: StorageEvent) => {
            if (evt.key === key) {
                setValue(evt.newValue as T);
            }
        };
        const customHandler = (evt: CustomEvent) => {
            if (evt.detail.key === key) {
                setValue(evt.detail.newValue);
            }
        };
        window.addEventListener("storage", handler);
        window.addEventListener("storage.custom", customHandler);

        return () => {
            window.removeEventListener("storage", handler);
            window.removeEventListener("storage.custom", customHandler);
        };
    });

    return useMemo(() => [
        value,
        (val: string) => {
            if (value !== val) {
                localStorage.setItem(key, val);
                const ev = new CustomEvent("storage.custom", {
                    detail: {
                        key: key,
                        newValue: val
                    }
                });
                window.dispatchEvent(ev);
            }
        }
    ], [value]);
}

export function useLocalStorageStrings(key: string): [string[], (val: string[]) => void] {

    const [raw, setRaw] = useLocalStorageString(key);

    return useMemo(() => {
        return [
            raw ? raw.split(",") : [],
            vals => {
                setRaw(vals.join(","));
            }
        ];
    }, [raw, setRaw]);
}

export function useLocalStorageEnums<TItem extends string>(key: string): [TItem[], (val: TItem[]) => void] {
    return useLocalStorageStrings(key) as [TItem[], (val: TItem[]) => void];
}

export function readLocalStorage<T>(key: string, defaultValue?: T) {
    const raw = localStorage.getItem(key);
    try {
        return raw ? JSON.parse(raw) as T : defaultValue;
    } catch {
        return defaultValue;
    }
}

export function writeLocalStorage<T>(key: string, value: T) {
    try {
        localStorage.setItem(key, JSON.stringify(value));
    } catch {
    }
}

export function useLocalStorageJsonFast<T>(key: string, defaultValue?: T): [T, Dispatch<SetStateAction<T>>] {

    return useMemo(() => {
        const obj = readLocalStorage<T>(key, defaultValue);

        return [
            obj,
            val => {
                if (typeof val === "function") {
                    val = (val as any)(obj);
                }
                writeLocalStorage(key, val);
            }
        ];
    }, [defaultValue]);
}

export function useLocalStorageJson<T>(key: string, defaultValue?: T): [T, Dispatch<SetStateAction<T>>] {

    const [raw, setRaw] = useLocalStorageString(key);

    return useMemo(() => {
        let obj: T;
        try {
            obj = raw ? JSON.parse(raw) as T : defaultValue;
        } catch {
            obj = defaultValue;
        }

        return [
            obj,
            val => {
                if (typeof val === "function") {
                    val = (val as any)(obj);
                }
                setRaw(JSON.stringify(val));
            }
        ];
    }, [raw, setRaw, defaultValue]);
}
